import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "sonner";
import { Scale, RefreshCw, PlayCircle, CheckCircle2, AlertTriangle, CreditCard, FileWarning, ExternalLink } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTs = (iso) => {
  if (!iso) return "never";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(iso); }
};
const fmtUnix = (s) => (s ? new Date(s * 1000).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminReconciliation() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [days, setDays] = useState(7);
  const [running, setRunning] = useState(false);
  const [resolving, setResolving] = useState("");

  const load = () => {
    setLoadErr(null);
    api.get("/admin/reconciliation")
      .then(({ data }) => setData(data))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load reconciliation."));
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data: summary } = await api.post("/admin/reconciliation/run", { days: Number(days) });
      if (summary.status === "stripe_unavailable") toast.error(summary.message || "Stripe is unavailable in this environment.");
      else if (summary.status === "error") toast.error(summary.message || "Reconciliation hit a Stripe error.");
      else toast.success(`Reconciliation complete — ${summary.matched} matched, ${summary.mismatched} mismatched.`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const resolve = async (flagId) => {
    setResolving(flagId);
    try {
      await api.post(`/admin/reconciliation/flags/${flagId}/resolve`, {});
      toast.success("Flag marked resolved.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setResolving(""); }
  };

  const lr = data?.last_run;
  const flags = data?.open_flags || [];
  const paymentFlags = flags.filter((f) => f.kind === "payment_no_order");
  const orderFlags = flags.filter((f) => f.kind === "order_no_payment");
  const stripeUnavailable = lr && lr.status === "stripe_unavailable";

  return (
    <div className="text-foreground" data-testid="admin-reconciliation">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
        <Scale className="w-7 h-7 text-olive-bright" /> Reconciliation
      </h2>
      <p className="text-sm font-body text-secondary-foreground mt-2 max-w-[720px]">
        Compares Stripe's paid checkouts against OpticSwap order records and surfaces mismatches — a
        real Stripe payment with no order (a missed webhook) or an order with no Stripe payment. Runs
        automatically every day at 3:00&nbsp;AM Central; run it on demand below. Read-only — orders are
        never created automatically; a missed payment is flagged here for you to create by hand.
      </p>
      <Crosshair className="my-5" />

      {loadErr && <LoadFailed reason={loadErr} onRetry={load} testid="recon-load-failed" />}

      {!loadErr && !data && (
        <div className="border border-sniper bg-surface p-10 text-center font-mono uppercase text-sm text-muted-foreground">Loading…</div>
      )}

      {!loadErr && data && (
        <>
          {/* controls */}
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <label className="font-mono text-[11px] uppercase tracking-[1.5px] text-muted-foreground">Window</label>
            <select
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="recon-days"
              className="bg-surface border border-sniper px-3 py-1.5 font-mono text-xs uppercase text-foreground outline-none focus:border-olive"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={run}
              disabled={running}
              data-testid="recon-run-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-mono uppercase text-xs tracking-wide transition-colors"
            >
              {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              {running ? "Running…" : "Run Reconciliation"}
            </button>
            <button
              onClick={load}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground transition-colors font-mono uppercase text-xs"
              data-testid="recon-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {stripeUnavailable && (
            <div className="border border-[#d3a24e]/60 bg-[#d3a24e]/5 p-4 mb-5 flex items-start gap-2" data-testid="recon-stripe-unavailable">
              <AlertTriangle className="w-4 h-4 text-[#d3a24e] mt-0.5 shrink-0" />
              <p className="text-sm font-body text-[#d3a24e]">Stripe isn't reachable in this environment, so the last run couldn't compare against Stripe. Run this in production.</p>
            </div>
          )}

          {/* summary */}
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <div className="border border-sniper bg-surface p-4" data-testid="recon-matched">
              <div className="font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">Matched</div>
              <div className="mt-1 font-heading text-2xl text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-olive-bright" /> {lr ? (lr.matched ?? 0) : "—"}
              </div>
            </div>
            <div className={`border p-4 ${lr && lr.mismatched > 0 ? "border-[#d3a24e]/60 bg-[#d3a24e]/5" : "border-sniper bg-surface"}`} data-testid="recon-mismatched">
              <div className="font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">Mismatched</div>
              <div className={`mt-1 font-heading text-2xl flex items-center gap-2 ${lr && lr.mismatched > 0 ? "text-[#d3a24e]" : "text-foreground"}`}>
                <AlertTriangle className="w-5 h-5" /> {lr ? (lr.mismatched ?? 0) : "—"}
              </div>
            </div>
            <div className="border border-sniper bg-surface p-4" data-testid="recon-lastrun">
              <div className="font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">Last run</div>
              <div className="mt-1 font-heading text-base text-foreground">{fmtTs(lr?.ran_at)}</div>
              {lr && <div className="font-mono text-[10px] uppercase text-muted-foreground mt-1">{lr.source} · {lr.days}d window</div>}
            </div>
          </div>

          {/* flags */}
          {flags.length === 0 ? (
            <div className="border border-olive/40 bg-olive/5 p-10 text-center" data-testid="recon-clean">
              <CheckCircle2 className="w-8 h-8 text-olive-bright mx-auto" />
              <p className="mt-3 font-heading text-lg text-foreground uppercase tracking-tight">No open mismatches</p>
              <p className="mt-1 text-sm font-body text-secondary-foreground">Every Stripe payment lines up with an order.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {paymentFlags.length > 0 && (
                <div data-testid="recon-payment-flags">
                  <h3 className="font-mono text-xs uppercase tracking-[2px] text-[#d3a24e] flex items-center gap-2 mb-3">
                    <CreditCard className="w-4 h-4" /> Stripe paid — no order ({paymentFlags.length}) · create by hand
                  </h3>
                  <div className="space-y-3">
                    {paymentFlags.map((f) => {
                      const d = f.details || {};
                      return (
                        <div key={f.flag_id} className="border border-[#d3a24e]/40 bg-surface p-4" data-testid={`recon-flag-${f.flag_id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="font-heading text-base text-foreground">{money(d.amount_cents)} <span className="text-xs font-mono uppercase text-muted-foreground">{d.order_type?.replace("_order", "") || "order"}</span></div>
                              <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                                <span>session {d.session_id}</span>
                                {d.payment_intent && <span>· {d.payment_intent}</span>}
                                {d.customer_email && <span>· {d.customer_email}</span>}
                                <span>· paid {fmtUnix(d.created)}</span>
                              </p>
                              {d.payment_intent && (
                                <a href={`https://dashboard.stripe.com/payments/${d.payment_intent}`} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-1 mt-2 text-[11px] font-mono uppercase text-olive-bright hover:underline">
                                  <ExternalLink className="w-3 h-3" /> Open in Stripe
                                </a>
                              )}
                            </div>
                            <button
                              onClick={() => resolve(f.flag_id)}
                              disabled={resolving === f.flag_id}
                              data-testid={`recon-resolve-${f.flag_id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground disabled:opacity-50 font-mono uppercase text-[11px] transition-colors"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {orderFlags.length > 0 && (
                <div data-testid="recon-order-flags">
                  <h3 className="font-mono text-xs uppercase tracking-[2px] text-[#d3a24e] flex items-center gap-2 mb-3">
                    <FileWarning className="w-4 h-4" /> Order — no Stripe payment ({orderFlags.length})
                  </h3>
                  <div className="space-y-3">
                    {orderFlags.map((f) => {
                      const d = f.details || {};
                      return (
                        <div key={f.flag_id} className="border border-[#d3a24e]/40 bg-surface p-4" data-testid={`recon-flag-${f.flag_id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <div className="font-heading text-base text-foreground">{d.order_id} <span className="text-xs font-mono uppercase text-muted-foreground">{d.order_type}</span></div>
                              <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                                <span>{money(d.amount_cents)}</span>
                                <span>· {String(d.status || "").replace(/_/g, " ")}</span>
                                {d.created_at && <span>· {String(d.created_at).slice(0, 10)}</span>}
                              </p>
                            </div>
                            <button
                              onClick={() => resolve(f.flag_id)}
                              disabled={resolving === f.flag_id}
                              data-testid={`recon-resolve-${f.flag_id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground disabled:opacity-50 font-mono uppercase text-[11px] transition-colors"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
